import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import brandManager from './brandManager'

// Flutter-style API service - exact match
class ApiService {
  private static instance: ApiService
  private _axios!: AxiosInstance
  private _storage = localStorage

  // Storage keys - exact match with Flutter
  private static readonly AUTH_TOKEN_KEY = 'auth_token'
  private static readonly USER_DATA_KEY = 'user_data'

  private constructor() {
    this._initializeAxios()
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService()
    }
    return ApiService.instance
  }

  private _initializeAxios(): void {
    this._axios = axios.create({
      baseURL: brandManager.apiUrl,
      headers: this._getDefaultHeaders(),
      maxRedirects: 3,
    })

    // Add interceptors for logging and error handling - exact match with Flutter
    this._axios.interceptors.request.use(
      (config) => {
        // Add auth token and language to every request - exact match with Flutter
        const authHeaders = this._getAuthHeaders()
        Object.assign(config.headers, authHeaders)
        
        // Debug logging for tables endpoint
        if (config.url?.includes('/cashier/tables')) {
        
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    this._axios.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        // Enhanced error logging for debugging - log ALL errors, especially 500s
        if (error.response) {
          const url = error.config?.url || 'unknown'
          const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
          const status = error.response?.status
          const errorData = error.response?.data
          const fullUrl = `${error.config?.baseURL || ''}${url}`
          
          // Log all 500 errors with comprehensive details
          if (status === 500) {
        
          } else if (status >= 400) {
    
          }
          
          // Special detailed logging for tables endpoint (keep existing behavior)
          if (url.includes('/cashier/tables')) {
         
          }
        } else if (error.request) {
          // Network error - no response received
         
        } else {
          // Request setup error
      
        }
        
        return Promise.reject(error)
      }
    )
  }

  /// Update API base URL when brand changes - exact match with Flutter
  updateBaseUrl(): void {
    const newBaseUrl = brandManager.apiUrl
    this._axios.defaults.baseURL = newBaseUrl
  }

  /// Get default headers for all requests - exact match with Flutter
  private _getDefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
  }

  /// Get authentication headers including token and language - exact match with Flutter
  private _getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}

    // Add auth token if available
    const token = this.getAuthToken()
    if (token && token.trim()) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Add current language - get from settings or localStorage
    let currentLang = 'ar' // default
    try {
      // First try to get from localStorage 'language' key (for backward compatibility)
      const langFromStorage = localStorage.getItem('language')
      if (langFromStorage) {
        currentLang = langFromStorage
      } else {
        // Try to get from app_settings
        const appSettings = localStorage.getItem('app_settings')
        if (appSettings) {
          const settings = JSON.parse(appSettings)
          currentLang = settings.language || 'ar'
        }
      }
    } catch (e) {
      // Fallback to default
      currentLang = 'ar'
    }
    headers['lang'] = currentLang

    // Add platform header based on user type
    headers['platform'] = this._getPlatformHeader()

    return headers
  }

  /// Get platform header based on user type - exact match with Flutter
  private _getPlatformHeader(): string {
    // Import getPlatformHeader from utils to ensure consistency
    try {
      // Dynamic import to avoid circular dependency
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { getPlatformHeader } = require('../utils/apiUtils')
      return getPlatformHeader()
    } catch (e) {
      // Fallback to local implementation
      if (this._isCustomerServiceUser()) {
        return 'customer_service'
      }
      return 'cashier'
    }
  }

  /// Check if current user is Customer Service - exact match with Flutter
  private _isCustomerServiceUser(): boolean {
    try {
      const employeeDataStr = this._storage.getItem('employee_data')

      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        const userType = employeeData.user_type || employeeData.type || ''
        return userType.toString().toLowerCase().includes('customer_service')
      }
      return false
    } catch (e) {
      return false
    }
  }

  /// Get stored authentication token - exact match with Flutter
  getAuthToken(): string | null {
    return this._storage.getItem(ApiService.AUTH_TOKEN_KEY)
  }

  /// Store authentication token - exact match with Flutter
  setAuthToken(token: string): void {
    this._storage.setItem(ApiService.AUTH_TOKEN_KEY, token)
  }

  /// Set temporary auth token for testing (without Bearer prefix) - exact match with Flutter
  setTempAuthToken(token: string): void {
    // Remove 'Bearer ' prefix if present
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token
    this._storage.setItem(ApiService.AUTH_TOKEN_KEY, cleanToken)
  }

  /// Clear authentication token - exact match with Flutter
  clearAuthToken(): void {
    this._storage.removeItem(ApiService.AUTH_TOKEN_KEY)
    this._storage.removeItem(ApiService.USER_DATA_KEY)
  }

  /// Check if user is authenticated - exact match with Flutter
  get isAuthenticated(): boolean {
    return this.getAuthToken() !== null
  }

  /// Store user data - exact match with Flutter
  setUserData(userData: Record<string, any>): void {
    this._storage.setItem(ApiService.USER_DATA_KEY, JSON.stringify(userData))
  }

  /// Get stored user data - exact match with Flutter
  getUserData(): Record<string, any> | null {
    const userDataStr = this._storage.getItem(ApiService.USER_DATA_KEY)
    if (userDataStr) {
      try {
        return JSON.parse(userDataStr)
      } catch (e) {
        return null
      }
    }
    return null
  }

  /// GET request - exact match with Flutter
  async get(
    path: string,
    queryParameters?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    try {
      // Add retry logic for better reliability - exact match with Flutter
      let retryCount = 0
      const maxRetries = 3

      while (retryCount < maxRetries) {
        try {
          return await this._axios.get(path, {
            params: queryParameters,
            ...options,
          })
        } catch (e) {
          retryCount++
          if (retryCount >= maxRetries) {
            throw e
          }
          // Wait before retry (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, retryCount * 2000))
        }
      }

      // This should never be reached, but just in case
      return await this._axios.get(path, {
        params: queryParameters,
        ...options,
      })
    } catch (e) {
      throw e
    }
  }

  /// POST request - exact match with Flutter
  async post(
    path: string,
    data?: any,
    queryParameters?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    try {
      return await this._axios.post(path, data, {
        params: queryParameters,
        ...options,
      })
    } catch (e) {
      throw e
    }
  }

  /// PUT request - exact match with Flutter
  async put(
    path: string,
    data?: any,
    queryParameters?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    try {
      return await this._axios.put(path, data, {
        params: queryParameters,
        ...options,
      })
    } catch (e) {
      throw e
    }
  }

  /// DELETE request - exact match with Flutter
  async delete(
    path: string,
    data?: any,
    queryParameters?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    try {
      return await this._axios.delete(path, {
        data,
        params: queryParameters,
        ...options,
      })
    } catch (e) {
      throw e
    }
  }

  /// PATCH request - exact match with Flutter
  async patch(
    path: string,
    data?: any,
    queryParameters?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    try {
      return await this._axios.patch(path, data, {
        params: queryParameters,
        ...options,
      })
    } catch (e) {
      throw e
    }
  }

  /// Get current Axios instance (for advanced usage) - exact match with Flutter
  get axios(): AxiosInstance {
    return this._axios
  }

  /// Get Axios defaults for direct header manipulation - exact match with Flutter
  get defaults() {
    return this._axios.defaults
  }

  /// Refresh Axios configuration (useful after environment changes) - exact match with Flutter
  refreshConfiguration(): void {
    this._initializeAxios()
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance()
export default apiService